import type { AuthContextValue, AuthResponse, User } from "@/types";
import {
  apiFetch,
  configureApi,
  handleResponse,
  resetApiSession,
} from "@/services/apiHandler";
import { API_CONFIG } from "../config/apiConfig";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";

const AuthContext = createContext<AuthContextValue | null>(null);
const ACCESS_TOKEN_KEY = "association_access_token";
const USER_KEY = "association_user";

export const getStoredUser = (): User | null => {
  try {
    const value =
      localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);

    // Preserve sessions created before this change, then use persistent
    // storage so a page reload does not send the user back to login.
    if (value && !localStorage.getItem(USER_KEY)) {
      localStorage.setItem(USER_KEY, value);
      sessionStorage.removeItem(USER_KEY);
    }

    return value ? (JSON.parse(value) as User) : null;
  } catch {
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
    return null;
  }
};

const getStoredAccessToken = (): string | null => {
  const token =
    localStorage.getItem(ACCESS_TOKEN_KEY) ||
    sessionStorage.getItem(ACCESS_TOKEN_KEY);

  if (token && !localStorage.getItem(ACCESS_TOKEN_KEY)) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  return token;
};

type ServerUser = {
  _id?: string;
  id?: string;
  official_email?: string;
  email?: string;
  association_name?: string;
  name?: string;
};

type ServerAuthResponse = {
  access_token?: string;
  accessToken?: string;
  two_factor_token?: string;
  association?: ServerUser;
  user?: User;
};
type AuthPayload =
  | ServerAuthResponse
  | { value: ServerAuthResponse; message?: string };

type NormalizedAuthPayload = {
  accessToken?: string;
  user?: User;
  two_factor_token?: string;
};

const getAuthPayload = async (
  response: Response,
): Promise<NormalizedAuthPayload> => {
  const body = (await response.json().catch(() => null)) as AuthPayload | null;

  if (!response.ok || !body) {
    const message = body && "message" in body ? body.message : undefined;
    throw new Error(message || "Authentication request failed");
  }

  const data = "value" in body ? body.value : body;

  // MFA required
  if (data.two_factor_token) {
    return {
      two_factor_token: data.two_factor_token,
    };
  }

  const accessToken = data.accessToken || data.access_token;

  if (!accessToken) {
    throw new Error("Authentication response is missing the access token");
  }

  const account = data.user || data.association;

  if (!account) {
    return {
      accessToken,
    };
  }

  const association = account as ServerUser;

  return {
    accessToken,
    user: {
      id: association.id || association._id || "",
      email: association.email || association.official_email || "",
      name: association.name || association.association_name,
    },
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // The refresh token remains an HttpOnly cookie. The access token is renewed
  // only after a 401 response and is restored after a browser reload.
  const tokenRef = useRef<string | null>(getStoredAccessToken());
  const userRef = useRef<User | null>(getStoredUser());
  const refreshRequestRef = useRef<Promise<string | null> | null>(null);
  const [user, setUser] = useState<User | null>(() => getStoredUser());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [otpLoading, setOtpLoading] = useState(false);

  const clearSession = useCallback(() => {
    tokenRef.current = null;
    userRef.current = null;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    if (refreshRequestRef.current) {
      return refreshRequestRef.current;
    }

    const request: Promise<string | null> = (async () => {
      try {
        const response = await apiFetch(
          API_CONFIG.endpoints.auth.refresh,
          { method: "POST" },
          false,
        );

        const data = await getAuthPayload(response);

        if (!data.accessToken) {
          throw new Error("Authentication response is missing access token");
        }

        tokenRef.current = data.accessToken;
        localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);

        resetApiSession();

        if (data.user) {
          userRef.current = data.user;
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
          setUser(data.user);
        } else if (!userRef.current) {
          clearSession();
          return null;
        }

        return data.accessToken;
      } catch {
        clearSession();
        return null;
      } finally {
        refreshRequestRef.current = null;
      }
    })();

    refreshRequestRef.current = request;
    return request;
  }, [clearSession]);

  useEffect(() => {
    configureApi(() => tokenRef.current, refresh, clearSession);
  }, [clearSession, refresh]);

  // const login = useCallback(
  //   async (identifier: string, password: string) => {
  //     setLoading(true);
  //     setError(null);

  //     try {
  //       const response = await apiFetch(
  //         API_CONFIG.endpoints.auth.login,
  //         { method: "POST", body: JSON.stringify({ identifier, password }) },
  //         false,
  //       );
  //       const data = await getAuthPayload(response);

  //       if (!data.user) {
  //         throw new Error("Login response is missing account details");
  //       }

  //       tokenRef.current = data.accessToken;
  //       userRef.current = data.user;
  //       localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
  //       localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  //       resetApiSession();
  //       setUser(data.user);
  //     } catch (caught) {
  //       clearSession();
  //       setError(
  //         caught instanceof Error ? caught.message : "Unable to sign in",
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [clearSession],
  // );

  const completeLogin = useCallback((data: NormalizedAuthPayload) => {
    if (!data.accessToken || !data.user) {
      throw new Error("Login response is missing account details");
    }

    tokenRef.current = data.accessToken;
    userRef.current = data.user;

    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));

    resetApiSession();
    setUser(data.user);
  }, []);

  const login = useCallback(
    async (identifier: string, password: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(
          API_CONFIG.endpoints.auth.login,
          {
            method: "POST",
            body: JSON.stringify({ identifier, password }),
          },
          false,
        );

        const data = await getAuthPayload(response);

        // MFA required
        if (data.two_factor_token) {
          navigate("/auth/two-factor", {
            state: {
              twoFactorToken: data.two_factor_token,
            },
          });
          return;
        }

        completeLogin(data);
      } catch (caught) {
        clearSession();
        setError(
          caught instanceof Error ? caught.message : "Unable to sign in",
        );
      } finally {
        setLoading(false);
      }
    },
    [clearSession, completeLogin, navigate],
  );

  const verifyMfa = useCallback(
    async (twoFactorToken: string, token: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(
          API_CONFIG.endpoints.auth.verifyMfa,
          {
            method: "POST",
            body: JSON.stringify({
              twoFactorToken: twoFactorToken,
              token: token,
            }),
          },
          false,
        );

        const data = await getAuthPayload(response);

        completeLogin(data);

        navigate("/");
      } catch (caught) {
        clearSession();
        setError(
          caught instanceof Error ? caught.message : "Unable to verify MFA",
        );
      } finally {
        setLoading(false);
      }
    },
    [clearSession, completeLogin, navigate],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch(
        API_CONFIG.endpoints.auth.logout,
        { method: "POST" },
        false,
      );
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const sendLoginOtp = useCallback(async (identifier: string) => {
    setOtpLoading(true);
    setError(null);

    try {
      const response = await apiFetch(
        API_CONFIG.endpoints.auth.sendLoginOtp,
        {
          method: "POST",
          body: JSON.stringify({
            identifier,
          }),
        },
        false,
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Unable to send OTP");
      }

      return data;
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Unable to send OTP";

      setError(message);
      throw new Error(message);
    } finally {
      setOtpLoading(false);
    }
  }, []);

  const verifyLoginOtp = useCallback(
    async (identifier: string, otp: string) => {
      setOtpLoading(true);
      setError(null);

      try {
        const response = await apiFetch(
          API_CONFIG.endpoints.auth.verifyLoginOtp,
          {
            method: "POST",
            body: JSON.stringify({
              identifier,
              otp,
            }),
          },
          false,
        );

        const data = await getAuthPayload(response);

        completeLogin(data);

        navigate("/");
      } catch (caught) {
        const message =
          caught instanceof Error ? caught.message : "Unable to verify OTP";

        setError(message);

        throw new Error(message);
      } finally {
        setOtpLoading(false);
      }
    },
    [completeLogin, navigate],
  );

  

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login, 
        logout,
        verifyMfa,
        sendLoginOtp,
        verifyLoginOtp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
